import { withHandlers, lifecycle } from 'recompose'
import moment from 'moment'

import { getDateTimeFormat } from 'i18n/utils'
import { userSelector } from 'auth/selectors'
import { fetchLocationDetails } from 'grs/actions'
import { detailsPendingSelector } from 'grs/selectors'
import { getRates, purchaseCompleted } from 'booking/actions'
import { selectedRateIdSelector, rateSelector, ratesPendingSelector } from 'booking/selectors'
import { startOfWeekSelector } from 'settings/selectors'
import { DATE_FORMAT } from '@constants'
import {
    reservationSelector,
    extendingDateTimeSelector,
    extendingErrorSelector,
    locationDetailsSelector,
    extendReservationPendingSelector,
} from '../selectors'
import { extendReservation, setExtensionDateTimeRange, resetExtendingProgress } from '../actions'

import { DatePicker, TimePicker, combineDateAndTime } from '@components/TimePicker'
import { CardSection } from '@components/CardSection'
import ErrorMessage from '@components/ErrorMessage'
import Button from '@components/Button'
import Select from '@components/Select'
import { RateCurrency } from 'shared/price-utilities'

import './styles.scss'

const Total = ({ rate: { amount: newRate, currencySymbol }, oldRate: { amount: oldRate } }) => (
    <table className="text-right ml-auto">
        <tbody>
            <tr>
                <td>
                    <T>extending.label.current</T>
                </td>
                <td>
                    <span className="ml-4">
                        <RateCurrency value={oldRate} currency={currencySymbol} />
                    </span>
                </td>
            </tr>
            <tr>
                <td>
                    <T>extending.label.total</T>
                </td>
                <td>
                    <RateCurrency value={newRate} currency={currencySymbol} />
                </td>
            </tr>
            <tr>
                <td className="pt-2">
                    <strong>
                        <T>extending.label.difference</T>
                    </strong>
                </td>
                <td className="text-xl pt-2">
                    <strong className="ml-4">
                        <RateCurrency value={newRate - parseInt(oldRate)} currency={currencySymbol} />
                    </strong>
                </td>
            </tr>
        </tbody>
    </table>
)

const ExtendView = ({
    onExtendDateRangeChange,
    onComplete,
    pending,
    reservation,
    user,
    ratesPending,
    rate,
    range: { end },
    extendingError,
    startOfWeek,
    locationDetails,
    locationDetailsPending,
}) => {
    const locationDetailsLoading = locationDetailsPending === true
    const ratesError = ratesPending instanceof Error && ratesPending.message
    const extendingInProgress = pending === true
    const ratesLoading = ratesPending === true
    const vehicle = reservation && reservation.vehicles && reservation.vehicles[0]
    const onEndDateChange = date => {
        const combined = combineDateAndTime(date, end)
        onExtendDateRangeChange(combined)
    }

    const onEndTimeChange = time => {
        const combined = combineDateAndTime(end, time)
        onExtendDateRangeChange(combined)
    }
    const getOptionsStartTime = () => {
        if (moment(reservation.toDate).isSame(end, 'day')) {
            return moment(reservation.toDate).add(30, 'minutes')
        }

        return null
    }

    return (
        <div className="d-flex extending">
            <div className="ml-auto mr-auto mt-5 mb-5 pl-2 pr-2 extending__container">
                {user ? (
                    reservation && reservation.locationId ? (
                        <>
                            <CardSection loading={locationDetailsLoading} title={<T>extending.title</T>}>
                                {locationDetails && (
                                    <div>
                                        <p className="mb-0">
                                            <strong>{locationDetails.name}</strong>
                                        </p>
                                        <p className="mb-0">{`${locationDetails.address1} ${locationDetails.address2} ${locationDetails.zip} ${locationDetails.city}`}</p>
                                        <p className="mb-0">{locationDetails.phone}</p>
                                        <p>{locationDetails.email}</p>
                                    </div>
                                )}
                                <p className="mb-0">
                                    <b>
                                        <T>label.parking.start</T>
                                    </b>
                                </p>
                                <p>{reservation.fromDate.format(getDateTimeFormat())}</p>

                                <p className="mb-0">
                                    <b>
                                        <T>label.parking.end</T>
                                    </b>
                                </p>
                                <p>{reservation.toDate.format(getDateTimeFormat())}</p>
                                <p className="mb-0">
                                    <b>
                                        <T>extending.label.vehicle</T>{' '}
                                    </b>
                                </p>
                                <p> {`${vehicle.make} ${vehicle.tag} ${vehicle.model} ${vehicle.color}`}</p>
                            </CardSection>
                            <CardSection
                                disabled={locationDetailsLoading}
                                title={<T>extending.title.select-end-time</T>}>
                                <div className="date-time row">
                                    <div className="col-sm-4">
                                        <DatePicker
                                            label={<T>label.parking.end</T>}
                                            value={moment(end)}
                                            onChange={onEndDateChange}
                                            displayFormat={DATE_FORMAT}
                                            startOfWeek={startOfWeek}
                                            disabled={extendingInProgress}
                                            isOutsideRange={day => day.isBefore(moment(reservation.toDate), 'day')}
                                        />
                                    </div>
                                    <div className="col-sm-4 mt-2 mt-sm-0">
                                        <TimePicker
                                            label={<T>label.datetime.info</T>}
                                            startTime={getOptionsStartTime()}
                                            value={moment(end)}
                                            onChange={onEndTimeChange}
                                            disabled={extendingInProgress}
                                        />
                                    </div>
                                </div>
                            </CardSection>
                            {ratesError && <ErrorMessage messages={[ratesError]} />}
                            {extendingError && <ErrorMessage messages={[extendingError]} />}
                            <CardSection
                                disabled={locationDetailsLoading}
                                loading={ratesLoading}
                                title={<T>extending.title.pay</T>}>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <Select
                                            isDisabled={true}
                                            value={{ label: `xxxx-xxxx-xxxx-${reservation.cardLast4}` }}
                                        />
                                    </div>
                                    <div className="col-sm-6 mt-4 mt-sm-0">
                                        <div className="ml-auto">
                                            {rate && <Total rate={rate} oldRate={reservation} />}
                                            <Button
                                                className="mt-4 ml-auto"
                                                size="large"
                                                onClick={onComplete}
                                                isLoading={extendingInProgress}
                                                disabled={ratesLoading || extendingInProgress}>
                                                <T>extending.button.pay</T>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CardSection>
                        </>
                    ) : (
                        <CardSection>
                            <h3>
                                <T>extending.error.no.location.text</T>
                            </h3>
                            <Button className="mt-3" btnStyle="link" href="/dashboard/transactions">
                                <T>extending.error.no.location.link</T>
                            </Button>
                        </CardSection>
                    )
                ) : (
                    <CardSection>
                        <h2>
                            <T>error.login.general</T>
                        </h2>
                    </CardSection>
                )}
            </div>
        </div>
    )
}

export default compose(
    connect(
        createStructuredSelector({
            user: userSelector,
            startOfWeek: startOfWeekSelector,

            rateId: selectedRateIdSelector,
            rate: rateSelector,
            ratesPending: ratesPendingSelector,

            reservation: reservationSelector,
            range: extendingDateTimeSelector,
            extendingError: extendingErrorSelector,

            pending: extendReservationPendingSelector,

            locationDetails: locationDetailsSelector,
            locationDetailsPending: detailsPendingSelector,
        }),
        {
            purchaseCompleted,
            getRates,
            setExtensionDateTimeRange,
            extendReservation,
            resetExtendingProgress,
            fetchLocationDetails,
        }
    ),
    lifecycle({
        componentDidMount() {
            const {
                reservation: { locationId, rateId, fromDate, toDate },
                getRates,
                setExtensionDateTimeRange,
                fetchLocationDetails,
            } = this.props

            if (locationId) {
                const extendingStart = moment(fromDate)
                const extendingEnd = moment(toDate).add(30, 'minutes')

                setExtensionDateTimeRange(extendingStart, extendingEnd)
                fetchLocationDetails(locationId)
                getRates({
                    grsId: locationId,
                    preselectedRateId: rateId,
                    extendingStart,
                    extendingEnd,
                    isExtending: true,
                })
            }
        },
        componentWillUnmount() {
            this.props.resetExtendingProgress()
        },
    }),
    withHandlers({
        onComplete:
            ({ history, reservation, extendReservation }) =>
            () => {
                extendReservation(reservation, history)
            },
        onExtendDateRangeChange:
            ({
                getRates,
                setExtensionDateTimeRange,
                reservation: { locationId: grsId, rateId: preselectedRateId, fromDate, toDate },
            }) =>
            newEndDate => {
                const extendingStart = moment(fromDate)
                const extendingEnd = newEndDate.isBefore(toDate) ? moment(toDate).add(30, 'minutes') : newEndDate

                setExtensionDateTimeRange(extendingStart, extendingEnd)
                getRates({ grsId, preselectedRateId, extendingStart, extendingEnd, isExtending: true })
            },
    })
)(ExtendView)
